import { PersonalInfoEditorViewModel } from '@/features/tax-profile/individual-profiles/PersonalInfoEditorViewModel'
import { action, observable, runInAction, computed } from 'mobx'
import type { DependentProfileVersion } from '@/features/tax-profile/individual-profiles/DependentProfile'
import { required, validationContext } from 'validx'
import {
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  validateObject,
} from '@/utils/validx-util'
import type { IndividualProfileStore } from '@/features/tax-profile/individual-profiles/IndividualProfileStore'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { task } from 'mobx-task'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'

export interface DependentProfileEditorViewModelOptions {
  /**
   * Whether the editor is in read-only mode.
   */
  isReadOnly?: () => boolean

  /**
   * Called when the profile has been saved.
   *
   * @param profile
   */
  onSaved(profile: DependentProfileVersion): Promise<unknown>

  /**
   * Called when the user wants to remove the dependent from
   * wherever it is linked.
   */
  onRemove(profile: DependentProfileVersion): unknown
}

/**
 * Editor for a dependent profile.
 */
export class DependentProfileEditorViewModel {
  readonly personalInfoVm = new PersonalInfoEditorViewModel()

  /**
   * Underlying modal state, for when the editor is displayed in a modal.
   */
  readonly modal = new ModalViewModel()

  /**
   * Observable that emits when a dependent is delisted.
   */
  readonly dependentDelisted$

  /**
   * Observable that emits when a dependent is added.
   */
  readonly dependentAdded$

  @observable accessor relationship = ''
  @observable accessor profile: DependentProfileVersion | null = null
  @observable accessor isNew = true

  /**
   * Whether the editor is in read-only mode.
   */
  @computed get isReadOnly() {
    return this._options.isReadOnly?.() ?? false
  }

  readonly validation = validationContext(this, {
    personalInfoVm: [validateObject()],
    relationship: [
      required('Relationship is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Relationship must not exceed 100 characters'),
    ],
  })

  constructor(
    private readonly _individualProfileStore: IndividualProfileStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _options: DependentProfileEditorViewModelOptions,
  ) {
    this.dependentDelisted$ = this._individualProfileStore.dependentDelisted$
    this.dependentAdded$ =
      this._individualProfileStore.dependentProfileVersions.additions$
  }

  /**
   * Resets the view model to the given profile or to a blank state.
   */
  @action.bound
  reset(profile?: DependentProfileVersion | null) {
    this.isNew = !profile?.id
    this.profile = profile ?? null
    this.relationship = profile?.relationship ?? ''
    this.personalInfoVm.reset(profile?.personalInfo)

    this.validation.reset()
  }

  /**
   * Opens the dialog, optionally with the given profile.
   */
  @action.bound
  showDialog(profile: DependentProfileVersion | null = null) {
    this.reset(profile)
    this.modal.show()
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  closeDialog() {
    this.modal.close()
  }

  /**
   * Saves the profile and closes the dialog.
   */
  save = task.resolved(async () => {
    this.validation.reset().validate()
    if (!this.validation.isValid) {
      return
    }

    await this._individualProfileStore
      .saveDependentProfileVersion({
        id: this.profile?.id,
        personalInfo: this.personalInfoVm.toPersonalInfo(),
        relationship: this.relationship,
      })
      .then((profile) => {
        runInAction(() => {
          this.profile = profile
        })
        return this._options.onSaved(profile)
      })
      .then(() => this.closeDialog())
      .catch(
        this._createSnackbarErrorHandler('Failed to save dependent profile'),
      )
  })

  /**
   * Calls the `onRemove` callback with the current profile.
   */
  @action.bound
  remove() {
    if (this.profile) {
      this._options.onRemove(this.profile)
    }
  }
}
